/**
 * Timeline activity/commitment renderer using a supplied ScheduleBlock and optional
 * already-looked-up TaskUI. Calendar events and protected holds disable dragging. Shows workspace
 * and P/RJ metadata, gates sessions on readiness, and supports supplied click handling and
 * Book-Tracker links.
 *
 * @module apps/web/src/components/calendar/ScheduledTaskBlock
 */

import { useDraggable } from '@dnd-kit/core';
import { CSS } from '@dnd-kit/utilities';
import type { ScheduleBlock, TaskUI } from '@fluxability/types';
import { cn, getEnergyColor, getPriorityColor } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { Clock, Zap, Play, Loader2, Calendar, BookOpen, Timer, Layers } from 'lucide-react';
import { ActivityDone, activityIsDone } from '@/components/activities/ActivityDone';
import { Checkbox } from '@/components/ui/checkbox';
import { useTasksStore } from '@/stores/tasksStore';
import { useScheduleStore } from '@/stores/scheduleStore';
import { useWorkspaceStore } from '@/stores/workspaceStore';
import { api } from '@/lib/api';
import { memo, useState } from 'react';
import { ActivitySessionLogger } from '@/components/activities/ActivitySessionLogger';
import { formatMissingPlanningFields, getMissingPlanningFields } from '@/lib/activityReadiness';
import { toast } from '@/hooks/use-toast';
import { laneStyle } from './timelineLanes';

/** Reuse ICU's local-clock formatter instead of rebuilding it four times per activity render. */
const calendarClock = new Intl.DateTimeFormat('en-US', {
  hour: 'numeric',
  minute: '2-digit',
  hour12: true,
});

/** Format in the browser timezone using the same en-US clock convention as the existing calendar. */
function formatTime(date: Date): string {
  return calendarClock.format(date);
}

/** Rendered placement plus an optional pre-indexed activity record and owner-selected click action. */
interface ScheduledTaskBlockProps {
  /** Timeline block with timestamps and activity/commitment identity. */
  block: ScheduleBlock;
  /** Optional activity pre-resolved by the parent to avoid per-cell store scans. */
  task?: TaskUI;
  /** Pixel height of an hour at the current zoom; defaults to the week calendar’s 60 px. */
  slotHeight?: number;
  /** Parent-owned action for editing a commitment/activity or confirming a suggestion. */
  onClick?: () => void;
  /** Zero-based side-by-side lane among overlapping blocks; defaults to 0. */
  lane?: number;
  /** Lanes sharing the column at this time; defaults to 1, the full width. */
  lanes?: number;
}

/**
 * Timeline activity/commitment renderer using a supplied ScheduleBlock and optional
 * already-looked-up TaskUI. Calendar events and protected holds disable dragging. Shows workspace
 * and P/RJ metadata, gates sessions on readiness, and supports supplied click handling and
 * Book-Tracker links.
 */
export const ScheduledTaskBlock = memo(function ScheduledTaskBlock(props: ScheduledTaskBlockProps) {
  return props.block.isGhost ? (
    <TentativeTaskBlock {...props} />
  ) : (
    <InteractiveScheduledTaskBlock {...props} />
  );
});

/** Render uncommitted placements without creating draggable/session/checkbox state for every preview. */
function TentativeTaskBlock({
  block,
  onClick,
  slotHeight = 60,
  lane,
  lanes,
}: ScheduledTaskBlockProps) {
  const start = new Date(block.startTime);
  const end = new Date(block.endTime);
  const timeLabel = `${formatTime(start)} - ${formatTime(end)}`;
  const compact = ((end.getTime() - start.getTime()) / 3600000) * slotHeight < 32;
  return (
    <div
      role="button"
      tabIndex={0}
      aria-pressed={false}
      aria-label={`${block.title}${block.priority ? ` Priority ${block.priority}` : ''} from ${formatTime(start)} to ${formatTime(end)} (Ghost block)`}
      data-mode={block.stateProfile?.mode}
      data-commitment={false}
      data-infringement={false}
      className="scheduled-block bg-muted/50 ghost-pulse focus:ring-primary absolute left-0 right-0 cursor-pointer overflow-hidden rounded-md border-2 border-dashed p-2 text-xs focus:outline-none focus:ring-2 focus:ring-offset-2"
      style={{
        top: `${(start.getMinutes() / 60) * 100}%`,
        height: `${Math.max((end.getTime() - start.getTime()) / 36000, 2)}%`,
        boxSizing: 'border-box',
        borderLeftWidth: '2px',
        zIndex: 1,
        minHeight: 0,
        padding: compact ? '0 4px' : undefined,
        ...laneStyle(lane, lanes),
      }}
      onClick={onClick}
      onKeyDown={event => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onClick?.();
        }
      }}
    >
      <div
        className="calendar-block-title truncate font-medium"
        title={`${block.title} · ${timeLabel}`}
        style={
          compact
            ? { display: 'block', whiteSpace: 'nowrap', fontSize: 11, lineHeight: '12px' }
            : undefined
        }
      >
        {block.title}
      </div>
      {!compact && (
        <div
          className="calendar-block-time bg-foreground/10 text-foreground mt-1 inline-flex w-fit max-w-full items-center gap-1 rounded-sm px-1.5 py-0.5 text-[10px] font-semibold leading-none"
          title={timeLabel}
        >
          <Clock className="h-3 w-3 shrink-0" />
          <span className="truncate">
            {timeLabel.replaceAll(' AM', 'a').replaceAll(' PM', 'p')}
          </span>
        </div>
      )}
    </div>
  );
}

/** Draggable subscription output; only stable fields cross into the expensive card subtree. */
type CalendarDragState = ReturnType<typeof useDraggable>;

/** Committed card inputs after isolating the frequently updated dnd-kit context subscription. */
interface CommittedTaskContentsProps extends ScheduledTaskBlockProps {
  /** Accessible draggable attributes, memoized by dnd-kit. */
  attributes: CalendarDragState['attributes'];
  /** Stable activator listeners, absent for disabled drag targets. */
  listeners: CalendarDragState['listeners'];
  /** Stable element-registration callback owned by the drag wrapper. */
  setNodeRef: CalendarDragState['setNodeRef'];
  /** CSS translation changes only during an actual drag. */
  transformStyle?: string;
  /** Whether this specific card is the current drag source. */
  isDragging: boolean;
}

/** Observe drag context without rerendering the full card when other blocks register themselves. */
const InteractiveScheduledTaskBlock = memo(function InteractiveScheduledTaskBlock(
  props: ScheduledTaskBlockProps
) {
  const { block } = props;
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: block.id,
    data: { type: 'ScheduledTask', block },
    disabled: Boolean(
      block.isCalendarEvent || block.isFreeTimeHold || block.isGhost || block.id.startsWith('gcal-')
    ),
  });
  return (
    <CommittedTaskContents
      {...props}
      attributes={attributes}
      listeners={listeners}
      setNodeRef={setNodeRef}
      transformStyle={CSS.Translate.toString(transform)}
      isDragging={isDragging}
    />
  );
});

/** Render committed activity controls; peer drag registration cannot invalidate these stable props. */
const CommittedTaskContents = memo(function CommittedTaskContents({
  block,
  task: taskProp,
  onClick,
  attributes,
  listeners,
  setNodeRef,
  transformStyle,
  isDragging,
  slotHeight = 60,
  lane,
  lanes,
}: CommittedTaskContentsProps) {
  const [isUpdating, setIsUpdating] = useState(false);
  const [isLoggingSession, setIsLoggingSession] = useState(false);
  // `toast` directly, not `useToast()`: the hook would re-render every block on each toast.
  const updateTask = useTasksStore(s => s.updateTask);
  const task = taskProp;
  const bundle = useWorkspaceStore(s => s.getBundle(task?.bundleId));
  const project = useWorkspaceStore(s => s.getProject(bundle?.projectId || task?.projectId));
  const startImmersionMode = useScheduleStore(s => s.startImmersionMode);

  const isCalendarEvent = block.isCalendarEvent || block.id.startsWith('gcal-');
  const isFreeTimeHold = block.isFreeTimeHold || block.id.startsWith('gcal-freehold:');
  const allowsConflict = isCalendarEvent && !isFreeTimeHold && Boolean(block.allowsConflict);
  const bookTrackerUrl = appendFlexTaskParam(getBookTrackerUrl(task?.customAttributes), task?.id);
  const missingPlanningFields = task ? getMissingPlanningFields(task) : [];
  const missingPlanningLabel = formatMissingPlanningFields(missingPlanningFields);
  const isPlanningComplete = missingPlanningFields.length === 0;
  const showPlanningRequiredToast = () => {
    toast({
      title: 'Fill planning details first',
      description: `Missing ${missingPlanningLabel}.`,
      variant: 'destructive',
    });
  };

  const isGhost = block.isGhost || false;
  const isDone = !isCalendarEvent && !isGhost && activityIsDone(task);
  const energyColor = block.requiredEnergy ? getEnergyColor(block.requiredEnergy) : undefined;
  const priorityColor = block.priority ? getPriorityColor(block.priority) : undefined;
  const commitmentProfile = block.stateProfile;
  const mergedCustomAttributes = {
    ...(block.customAttributes || {}),
    ...(task?.customAttributes || {}),
  };
  const isDeadlineViolation =
    !isGhost &&
    !isCalendarEvent &&
    Boolean(
      mergedCustomAttributes.deadline_violation ||
      mergedCustomAttributes.vacation_deadline_override ||
      mergedCustomAttributes.deadline_violation_level === 'vacation_deadline_override'
    );
  const isVacationOverride =
    isDeadlineViolation &&
    Boolean(
      mergedCustomAttributes.vacation_deadline_override ||
      mergedCustomAttributes.deadline_violation_level === 'vacation_deadline_override'
    );

  // Calculate position and height based on time
  const startDate = new Date(block.startTime);
  const endDate = new Date(block.endTime);
  const timeLabel = `${formatTime(startDate)} - ${formatTime(endDate)}`;
  const durationMinutes = (endDate.getTime() - startDate.getTime()) / (1000 * 60);

  // Position from top relative to the hour cell it is placed in
  const startMinuteOfHour = startDate.getMinutes();
  const topPosition = (startMinuteOfHour / 60) * 100; // 60 minutes in an hour
  const height = (durationMinutes / 60) * 100;
  const compact = (durationMinutes / 60) * slotHeight < 32;

  const style = {
    top: `${topPosition}%`,
    height: `${Math.max(height, 2)}%`,
    boxSizing: 'border-box' as const,
    minHeight: 0,
    padding: compact ? '0 4px' : undefined,
    paddingRight: compact && !isCalendarEvent ? 16 : undefined,
    borderLeftWidth: isGhost ? '2px' : '4px',
    borderLeftColor: isDeadlineViolation
      ? '#ef4444'
      : isGhost
        ? undefined
        : bundle?.color || priorityColor,
    transform: transformStyle,
    zIndex: isDragging ? 50 : 1,
    opacity: isDragging ? 0.8 : 1,
    touchAction: isCalendarEvent || isFreeTimeHold ? 'manipulation' : 'none',
    ...laneStyle(lane, lanes),
  };

  const handleComplete = async (checked: boolean) => {
    if (isGhost || !block.taskId) return;

    if (checked) {
      if (!isPlanningComplete) {
        showPlanningRequiredToast();
        return;
      }
      startImmersionMode(block.taskId);
      return;
    }

    const reopenedCompletion = Math.min(99, Math.max(0, task?.completionPercent ?? 99));
    setIsUpdating(true);
    updateTask(block.taskId, {
      status: reopenedCompletion > 0 ? 'in_progress' : 'todo',
      completionPercent: reopenedCompletion,
    });
    try {
      await api.tasks.update(block.taskId, {
        status: reopenedCompletion > 0 ? 'in_progress' : 'todo',
        completion_percent: reopenedCompletion,
        completed_at: null,
      });
    } catch (err) {
      console.error('Failed to reopen activity', err);
      updateTask(block.taskId!, {
        status: task?.status || 'completed',
        completionPercent: task?.completionPercent ?? 100,
      });
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <div
      ref={setNodeRef}
      data-mode={task?.stateProfile?.mode || block.stateProfile?.mode}
      data-commitment={isCalendarEvent}
      data-allows-conflict={allowsConflict}
      data-infringement={isDeadlineViolation}
      {...(!isCalendarEvent ? listeners : {})}
      {...(!isCalendarEvent ? attributes : {})}
      role="button"
      tabIndex={0}
      aria-label={`${block.title}${block.priority ? ` Priority ${block.priority}` : ''} from ${formatTime(new Date(block.startTime))} to ${formatTime(new Date(block.endTime))}${isDone ? ' (Done)' : ''}${isGhost ? ' (Ghost block)' : ''}${isCalendarEvent ? ' (Calendar event)' : ''}${allowsConflict ? ' (activities may overlap)' : ''}`}
      aria-pressed={false}
      onClick={onClick}
      onKeyDown={event => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onClick?.();
        }
      }}
      className={cn(
        'scheduled-block focus:ring-primary absolute left-0 right-0 overflow-hidden rounded-md p-2 text-xs transition-all hover:shadow-md focus:outline-none focus:ring-2 focus:ring-offset-2',
        !isGhost && !isCalendarEvent && 'pr-9',
        isDragging ? 'cursor-grabbing' : 'cursor-pointer',
        isGhost
          ? 'bg-muted/50 ghost-pulse border-2 border-dashed'
          : isCalendarEvent
            ? isFreeTimeHold
              ? 'border border-emerald-200 bg-emerald-50 dark:border-emerald-800 dark:bg-emerald-950'
              : allowsConflict
                ? 'border-2 border-dashed border-blue-400 bg-blue-50/60 dark:border-blue-500 dark:bg-blue-950/60'
                : 'border border-blue-200 bg-blue-50 dark:border-blue-800 dark:bg-blue-950'
            : isDeadlineViolation
              ? 'border border-red-400 bg-red-50 text-red-950 shadow-sm dark:border-red-700 dark:bg-red-950/70 dark:text-red-50'
              : 'bg-card border shadow-sm'
      )}
      style={style}
    >
      {!isGhost && !isCalendarEvent && block.taskId && (
        <button
          type="button"
          className="calendar-immersion absolute right-1.5 top-1.5 z-20 flex h-7 w-7 items-center justify-center rounded-full border border-cyan-300/60 bg-cyan-300 text-slate-950 shadow-sm transition hover:bg-cyan-200 focus:outline-none focus:ring-2 focus:ring-cyan-300 focus:ring-offset-2"
          style={
            compact
              ? { top: 0, bottom: 'auto', right: 1, width: 12, height: 12, borderRadius: 2 }
              : undefined
          }
          title="Start immersive activity mode"
          aria-label={`Start immersive mode for ${block.title}`}
          onPointerDown={e => e.stopPropagation()}
          onClick={e => {
            e.stopPropagation();
            if (!isPlanningComplete) {
              showPlanningRequiredToast();
              return;
            }
            startImmersionMode(block.taskId!);
          }}
          disabled={!isPlanningComplete}
        >
          <Play className="h-3.5 w-3.5 fill-current" />
        </button>
      )}
      {compact ? (
        <div
          className="calendar-block-title flex min-w-0 items-center gap-1 font-medium"
          title={`${block.title} · ${timeLabel}`}
          style={{ whiteSpace: 'nowrap', fontSize: 11, lineHeight: '12px' }}
        >
          {isDone && <ActivityDone compact />}
          <span className={cn('min-w-0 truncate', isDone && 'text-muted-foreground line-through')}>{block.title}</span>
        </div>
      ) : (
        <div className="flex h-full items-start gap-2">
          {isCalendarEvent ? (
            <div className={cn('pt-0.5', isFreeTimeHold ? 'text-emerald-500' : 'text-blue-500')}>
              <Calendar className="h-4 w-4" />
            </div>
          ) : (
            !isGhost && (
              <div
                className="calendar-progress-control pt-0.5"
                onPointerDown={e => e.stopPropagation()}
              >
                {isUpdating ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Checkbox
                    checked={isDone}
                    onCheckedChange={handleComplete}
                    aria-label={`${isDone ? 'Reopen' : 'Log progress for'} ${block.title}`}
                  />
                )}
              </div>
            )
          )}
          <div className="flex h-full min-w-0 flex-1 flex-col">
            <div className="flex items-start justify-between gap-2">
              <div className="flex min-w-0 items-center gap-1">
                {isDone && <ActivityDone />}
                <div className={cn('calendar-block-title truncate font-medium', isDone && 'text-muted-foreground line-through')} title={block.title}>
                  {block.title}
                </div>
              </div>
              {!isGhost && !isCalendarEvent && block.priority && (
                <Badge
                  variant="outline"
                  className={cn(
                    'calendar-block-badge h-4 flex-shrink-0 px-1 text-[10px]',
                    isDeadlineViolation && 'border-red-500 text-red-700 dark:text-red-200'
                  )}
                  style={
                    isDeadlineViolation
                      ? undefined
                      : { borderColor: priorityColor, color: priorityColor }
                  }
                >
                  P{block.priority}
                </Badge>
              )}
              {isDeadlineViolation && (
                <Badge
                  variant="outline"
                  className="h-4 flex-shrink-0 border-red-500 bg-red-100 px-1 text-[10px] text-red-700 dark:bg-red-950 dark:text-red-200"
                >
                  {isVacationOverride ? 'Vacation' : 'Deadline'}
                </Badge>
              )}
              {isCalendarEvent && (
                <Badge
                  variant="outline"
                  className={cn(
                    'calendar-block-badge h-4 flex-shrink-0 px-1 text-[10px]',
                    isFreeTimeHold
                      ? 'border-emerald-300 text-emerald-600 dark:border-emerald-700 dark:text-emerald-400'
                      : 'border-blue-300 text-blue-600 dark:border-blue-700 dark:text-blue-400'
                  )}
                >
                  {isFreeTimeHold ? 'Hold' : 'GCal'}
                </Badge>
              )}
              {allowsConflict && (
                <span
                  className="flex-shrink-0 text-blue-600 dark:text-blue-300"
                  title="Activities may overlap this event"
                >
                  <Layers className="h-3.5 w-3.5" aria-hidden="true" />
                </span>
              )}
            </div>
            <div
              className={cn(
                'calendar-block-time mt-1 inline-flex w-fit max-w-full items-center gap-1 rounded-sm px-1.5 py-0.5 text-[10px] font-semibold leading-none',
                isCalendarEvent
                  ? isFreeTimeHold
                    ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900 dark:text-emerald-100'
                    : 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-100'
                  : 'bg-foreground/10 text-foreground'
              )}
              title={timeLabel}
            >
              <Clock className="h-3 w-3 shrink-0" />
              <span className="truncate">
                {timeLabel.replaceAll(' AM', 'a').replaceAll(' PM', 'p')}
              </span>
            </div>
            {isCalendarEvent && commitmentProfile && (
              <div className="mt-1 flex flex-wrap gap-1 text-[10px] text-blue-700 dark:text-blue-300">
                {commitmentProfile.required_productivity_energy != null && (
                  <span>P req {commitmentProfile.required_productivity_energy}</span>
                )}
                {commitmentProfile.required_rejuvenation_energy != null && (
                  <span>RJ req {commitmentProfile.required_rejuvenation_energy}</span>
                )}
                {commitmentProfile.resistance != null && (
                  <span>R {commitmentProfile.resistance}</span>
                )}
              </div>
            )}
            {(bundle || project) && !isCalendarEvent && (
              <div className="text-muted-foreground mt-1 flex min-w-0 items-center gap-1.5 text-[10px]">
                {bundle && (
                  <span className="truncate" style={{ color: bundle.color }}>
                    {bundle.name}
                  </span>
                )}
                {project && <span className="truncate">/ {project.name}</span>}
              </div>
            )}
            <div className="calendar-block-detail text-muted-foreground mt-auto flex items-center gap-2 pt-1 text-[10px]">
              <div className="flex items-center gap-0.5">
                <Clock className="h-3 w-3" />
                {timeLabel}
              </div>
              {block.requiredEnergy && (
                <div className="flex items-center gap-0.5" style={{ color: energyColor }}>
                  <Zap className="h-3 w-3" />
                  {block.requiredEnergy}
                </div>
              )}
              {!isGhost && !isCalendarEvent && block.taskId && (
                <div className="ml-auto flex shrink-0 items-center gap-1">
                  {bookTrackerUrl && (
                    <button
                      className="hover:bg-secondary hover:text-primary focus:ring-primary flex items-center gap-1 rounded-sm px-1 py-0.5 text-[10px] font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2"
                      title="Open reading session in Book-Tracker"
                      aria-label={`Open ${block.title} in Book-Tracker`}
                      onPointerDown={e => e.stopPropagation()}
                      onClick={e => {
                        e.stopPropagation();
                        window.location.href = bookTrackerUrl;
                      }}
                    >
                      <BookOpen className="h-3 w-3" />
                      Read
                    </button>
                  )}
                  {task && (
                    <button
                      className="hover:bg-secondary hover:text-primary focus:ring-primary flex items-center gap-1 rounded-sm px-1 py-0.5 text-[10px] font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2"
                      title="Log activity session"
                      aria-label={`Log session for ${block.title}`}
                      onPointerDown={e => e.stopPropagation()}
                      onClick={e => {
                        e.stopPropagation();
                        if (!isPlanningComplete) {
                          showPlanningRequiredToast();
                          return;
                        }
                        setIsLoggingSession(true);
                      }}
                      disabled={!isPlanningComplete}
                    >
                      <Timer className="h-3 w-3" />
                      Log
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      {isLoggingSession && (
        <ActivitySessionLogger
          task={task || null}
          open={isLoggingSession}
          onClose={() => setIsLoggingSession(false)}
          onLogged={updatedTask => {
            updateTask(updatedTask.id, updatedTask);
            setIsLoggingSession(false);
          }}
        />
      )}
    </div>
  );
});

/** Resolve retained Book-Tracker metadata to the historical reader destination, preferring a paper ID over a book ID. Non-reader activities return null; this helper does not select a new company host. */
function getBookTrackerUrl(customAttributes?: Record<string, unknown>): string | null {
  if (!customAttributes) return null;
  const bridge =
    typeof customAttributes.book_tracker === 'object' && customAttributes.book_tracker
      ? (customAttributes.book_tracker as Record<string, unknown>)
      : customAttributes;

  const source = String(customAttributes.source || bridge.source || '').toLowerCase();
  if (source !== 'book-tracker' && !customAttributes.book_tracker) return null;

  const paperId = bridge.paperId ?? bridge.paper_id;
  if (paperId != null && String(paperId).trim()) {
    return `https://saireader.sahastasai.com/read/paper/${encodeURIComponent(String(paperId))}`;
  }

  const bookId = bridge.bookId ?? bridge.book_id;
  if (bookId != null && String(bookId).trim()) {
    return `https://saireader.sahastasai.com/read/${encodeURIComponent(String(bookId))}`;
  }

  return 'https://saireader.sahastasai.com/';
}

/** Add historical reader-return context to a known URL when an activity ID exists. Missing inputs are unchanged; an invalid nonempty URL throws. */
function appendFlexTaskParam(url: string | null, taskId?: string): string | null {
  if (!url || !taskId) return url;
  const parsed = new URL(url);
  parsed.searchParams.set('from', 'flex');
  parsed.searchParams.set('flexTaskId', taskId);
  return parsed.toString();
}
